import React from 'react';
import { Navigate } from 'react-router-dom';
import { authService } from '../services/authService';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: Array<'ADMIN' | 'OPERATOR' | 'CLIENT'>;
  redirectTo?: string;
}

/**
 * ProtectedRoute component - wraps routes that require authentication
 *
 * Usage:
 * <Route path="/crm" element={<ProtectedRoute requiredRoles={['ADMIN', 'OPERATOR']}><CRMDashboard /></ProtectedRoute>} />
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles,
  redirectTo = '/login',
}) => {
  const isAuthenticated = authService.isAuthenticated();
  const currentUser = authService.getCurrentUser();

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  // Check if user has required role
  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole = authService.hasAnyRole(requiredRoles);

    if (!hasRequiredRole) {
      // User doesn't have required role - redirect based on their actual role
      if (currentUser) {
        switch (currentUser.role) {
          case 'ADMIN':
          case 'OPERATOR':
            return <Navigate to="/crm" replace />;
          case 'CLIENT':
            return <Navigate to="/client/applications" replace />;
          default:
            return <Navigate to="/unauthorized" replace />;
        }
      }

      return <Navigate to="/unauthorized" replace />;
    }
  }

  // All checks passed - render children
  return <>{children}</>;
};
